import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { noteDocName, type AppToolMeta } from '@awwyeah/shared';
import { usePresence, useYDoc, useYSnapshot } from '../../sync';
import { useUser } from '../auth';
import { applyTextDiff } from './textBinding';
import { useAppToolMeta } from './useAppToolRegistries';

/**
 * NotePad (WS-16). Plain text on `Y.Text` — no markdown rendering (v1 scope). The textarea
 * is a fully controlled component whose value *is* `ytext.toString()`; every change goes
 * through `applyTextDiff`, a minimal common-prefix/suffix diff so two members typing in
 * different parts of the note merge instead of stomping each other (see `textBinding.ts`).
 */
export function NotePadPage(): JSX.Element {
  const { toolId } = useParams<{ toolId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const { meta, map } = useAppToolMeta(toolId);
  const docName = toolId ? noteDocName(toolId) : null;
  const doc = useYDoc(docName ?? noteDocName('missing'));
  const ytext = doc.getText('body');
  const value = useYSnapshot(ytext, () => ytext.toString());
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const others = usePresence(
    docName ?? '',
    user && docName ? { userId: user.id, name: user.name, color: user.color } : null,
  );

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    doc.transact(() => applyTextDiff(ytext, e.target.value));
    autosize();
  };

  const autosize = (): void => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const renameTo = (title: string): void => {
    if (!meta || !map || !user || !title.trim()) return;
    map.set(meta.id, {
      ...meta,
      title: title.trim(),
      updatedBy: user.id,
      updatedAt: new Date().toISOString(),
    } satisfies AppToolMeta);
  };

  if (!toolId) return <></>;

  return (
    <div className="flex flex-col gap-4 py-4" data-testid="notepad-page">
      <header className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Back to Apps"
          data-testid="notepad-back"
          onClick={() => navigate('/apps')}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center text-fg-2"
        >
          <ArrowLeft size={20} />
        </button>
        <input
          data-testid="notepad-title-input"
          className="h-11 min-w-0 flex-1 rounded border border-transparent bg-transparent px-2 text-xl font-bold text-fg focus:border-line-strong focus:bg-bg"
          value={meta?.title ?? ''}
          placeholder="Untitled note"
          onChange={(e) => renameTo(e.target.value)}
        />
        <div className="flex -space-x-2" data-testid="notepad-presence">
          {others.map((presence) => (
            <span
              key={presence.userId}
              title={presence.name}
              data-testid="presence-dot"
              className="h-6 w-6 flex-shrink-0 rounded-full border-2 border-surface"
              style={{ background: presence.color }}
            />
          ))}
        </div>
      </header>

      <textarea
        ref={textareaRef}
        data-testid="notepad-textarea"
        className="min-h-[50vh] flex-1 resize-none whitespace-pre-wrap rounded-md border border-line bg-surface p-4 text-base leading-relaxed text-fg"
        value={value}
        onChange={onChange}
        placeholder="Start typing…"
        autoFocus
      />
    </div>
  );
}
